const scenarios = [
  {
    title: 'The Active Scalper',
    tag: 'High frequency · ECN',
    icon: 'ri-flashlight-fill',
    color: '#2563EB',
    totalCost: '$3,300',
    period: '/month',
    breakdown: [
      { label: 'Spread', value: '$990', pct: 30, color: '#2563EB' },
      { label: 'Commission', value: '$2,310', pct: 70, color: '#00A6B4' },
    ],
    stats: [
      { label: 'Per trade', value: '$10', unit: '' },
      { label: 'Per day', value: '$150', unit: '' },
      { label: 'Round-turns', value: '15', unit: '/day' },
    ],
    insight: 'Commission is 3.3× the spread. An ECN account with tight spread + commission beats "commission-free" 1.0p spread by ~$1,200/month.',
  },
  {
    title: 'The News Day Trader',
    tag: 'Moderate frequency · Standard',
    icon: 'ri-sun-fill',
    color: '#00A6B4',
    totalCost: '$1,320',
    period: '/month',
    breakdown: [
      { label: 'Spread', value: '$880', pct: 67, color: '#00A6B4' },
      { label: 'Slippage', value: '$440', pct: 33, color: '#7C3AED' },
    ],
    stats: [
      { label: 'Per trade', value: '$20', unit: '' },
      { label: 'Per day', value: '$60', unit: '' },
      { label: 'Round-turns', value: '3', unit: '/day' },
    ],
    insight: 'One slipped market order during news adds $20, or 33% to daily cost. Switching to limit orders saves ~$5,300/year with zero strategy changes.',
  },
  {
    title: 'The Swing Trader',
    tag: 'Low frequency · Multi-week hold',
    icon: 'ri-moon-fill',
    color: '#F59E0B',
    totalCost: '$282',
    period: '/month',
    breakdown: [
      { label: 'Swap', value: '$230', pct: 82, color: '#F59E0B' },
      { label: 'Spread', value: '$52', pct: 18, color: '#2563EB' },
    ],
    stats: [
      { label: 'Per position', value: '$94', unit: '' },
      { label: 'Swap/day', value: '$7.70', unit: '' },
      { label: 'Positions', value: '3', unit: '/month' },
    ],
    insight: 'Swap is 4.4× the spread. A swap-free account cuts costs 81%, but watch for wider spreads or admin fees that partially offset the savings.',
  },
];

export default function RealTradeExamples() {
  return (
    <section className="w-full bg-background-100 overflow-hidden">
      {/* Decorative top element */}
      <div className="w-full max-w-main mx-auto px-4 md:px-6">
        <div className="h-[1px] bg-gradient-to-r from-transparent via-[#E6EAF0] to-transparent"></div>
      </div>

      <div className="w-full max-w-main mx-auto px-4 md:px-6 py-[80px] md:py-[110px]">
        {/* Header: big number lede */}
        <div className="flex flex-col lg:flex-row lg:items-end gap-6 lg:gap-12 mb-10 md:mb-14">
          <div className="flex-shrink-0">
            <div className="flex items-baseline gap-2">
              <span className="text-[72px] md:text-[88px] font-[800] leading-none tracking-[-0.04em] text-[#101828]">3</span>
              <span className="text-[16px] font-bold text-[#667085] uppercase tracking-[0.1em]">real<br/>traders</span>
            </div>
          </div>
          <div className="flex-1">
            <h2 className="text-[28px] md:text-[38px] font-[750] leading-[36px] md:leading-[46px] text-[#101828] tracking-[-0.015em] max-w-[500px]">
              Same market, same lot size — completely different cost profiles.
            </h2>
          </div>
          <p className="text-[14px] leading-[22px] text-[#667085] max-w-[260px] flex-shrink-0 lg:text-right">
            Three traders. One standard lot. The math doesn&apos;t lie.
          </p>
        </div>

        {/* Three scenario cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 md:gap-6">
          {scenarios.map((scenario) => (
            <div
              key={scenario.title}
              className="flex flex-col rounded-3xl border border-[#E6EAF0] bg-white overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:border-[#D1D8E0]"
            >
              {/* Top: title banner */}
              <div className="px-6 pt-6 md:px-7 md:pt-7">
                <div className="flex items-center gap-3 mb-1">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${scenario.color}14` }}
                  >
                    <i className={`${scenario.icon} text-lg`} style={{ color: scenario.color }}></i>
                  </div>
                  <div>
                    <h3 className="text-[17px] font-[750] leading-[22px] text-[#101828]">{scenario.title}</h3>
                    <span className="text-[11px] font-semibold text-[#94A3B8]">{scenario.tag}</span>
                  </div>
                </div>
              </div>

              {/* Big cost number + visual bar group */}
              <div className="px-6 md:px-7">
                <div className="flex items-baseline gap-1 mb-4">
                  <span
                    className="text-[44px] md:text-[52px] font-[800] leading-none tracking-[-0.03em]"
                    style={{ color: scenario.color }}
                  >
                    {scenario.totalCost}
                  </span>
                  <span className="text-[16px] font-semibold text-[#94A3B8]">{scenario.period}</span>
                </div>

                {/* Visual bar */}
                <div className="flex h-[8px] rounded-full overflow-hidden bg-[#F2F4F8] mb-3">
                  {scenario.breakdown.map((item, idx) => (
                    <div
                      key={item.label}
                      className="h-full transition-all duration-700"
                      style={{
                        width: `${item.pct}%`,
                        backgroundColor: item.color,
                        borderRadius:
                          idx === 0
                            ? '9999px 0 0 9999px'
                            : idx === scenario.breakdown.length - 1
                              ? '0 9999px 9999px 0'
                              : '0',
                      }}
                    ></div>
                  ))}
                </div>

                {/* Legend: compact inline */}
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                  {scenario.breakdown.map((item) => (
                    <div key={item.label} className="flex items-center gap-1.5">
                      <span className="w-[7px] h-[7px] rounded-full flex-shrink-0" style={{ backgroundColor: item.color }}></span>
                      <span className="text-[11px] text-[#94A3B8]">{item.label}</span>
                      <span className="text-[12px] font-[700] text-[#101828]">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Divider */}
              <div className="mx-6 md:mx-7 mt-5 border-t border-[#F2F4F8]"></div>

              {/* Stat strip: 3 compact numbers */}
              <div className="px-6 md:px-7 py-5">
                <div className="grid grid-cols-3 gap-3">
                  {scenario.stats.map((stat) => (
                    <div key={stat.label} className="text-center">
                      <span className="block text-[19px] md:text-[21px] font-[750] leading-none text-[#101828] tracking-[-0.01em]">
                        {stat.value}
                        {stat.unit && <span className="text-[12px] font-semibold text-[#94A3B8] ml-0.5">{stat.unit}</span>}
                      </span>
                      <span className="block text-[10.5px] font-semibold text-[#94A3B8] uppercase tracking-[0.05em] mt-1.5">
                        {stat.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Divider */}
              <div className="mx-6 md:mx-7 border-t border-[#F2F4F8]"></div>

              {/* Insight: one tight line */}
              <div className="px-6 md:px-7 py-4 flex items-start gap-2.5 flex-1">
                <span
                  className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-[7px]"
                  style={{ backgroundColor: scenario.color }}
                ></span>
                <p className="text-[12.5px] leading-[19px] text-[#475467]">
                  {scenario.insight}
                </p>
              </div>

              {/* Bottom accent */}
              <div className="h-[3px]" style={{ backgroundColor: scenario.color }}></div>
            </div>
          ))}
        </div>

        <p className="mt-10 text-center text-[12px] leading-[20px] text-[#94A3B8] max-w-[600px] mx-auto">
          Examples use typical retail conditions and 1 standard lot for illustration. Actual costs vary by broker, pair, and market conditions.
        </p>
      </div>
    </section>
  );
}